import React, { useState } from 'react';
import { eventTypes, siteConfig } from '../data/mock';
import {
  Send,
  Mail,
  Phone,
  MapPin,
  Calendar,
  MessageSquare,
  User,
  CheckCircle,
  Instagram,
  Facebook,
  Music2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, eventType: value }));
  };

  const encode = (data) => new URLSearchParams(data).toString();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const body = encode({
        'form-name': 'contact',
        ...formData,
        eventLocation: 'Florida'
      });

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
      });

      setShowSuccess(true);
      setSubmitMessage(
        "Thank you! Your inquiry has been received. We'll be in touch soon to start planning your beautiful event."
      );
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        eventDate: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(
        'There was an issue sending your message. Please try again or email us directly at Hemofgraceevents@gmail.com'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 bg-gradient-to-b from-white via-lavender-50/30 to-lavender-100/50 relative overflow-hidden"
    >
      {/* 3D Floral background decorations */}
      <div className="absolute top-20 right-10 w-80 h-80">
        <div className="absolute w-56 h-56 bg-lavender-200/40 rounded-full blur-3xl" />
        <div className="absolute top-10 right-10 w-8 h-16 bg-lilac-300/30 rounded-full blur-[2px] rotate-[20deg] floral-accent" />
      </div>
      <div className="absolute bottom-20 left-10 w-64 h-64">
        <div className="absolute w-48 h-48 bg-lilac-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-6 h-12 bg-violet-200/25 rounded-full blur-[2px] -rotate-[35deg] floral-accent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block text-lavender-600 text-sm tracking-[0.2em] uppercase mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            Get In Touch
          </span>
          <h2
            className="text-4xl sm:text-5xl text-grape-800 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Let's Create Something Beautiful
          </h2>
          <p
            className="text-grape-600/70 text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            Ready to start planning your graceful event in Florida? Fill out the form below and we'll be in
            touch soon.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Card */}
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-br from-lavender-200/30 to-transparent rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative bg-white rounded-2xl p-8 shadow-xl shadow-lavender-900/10 border border-lavender-100/50">
                <h3
                  className="text-2xl text-grape-800 mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Contact Information
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-lavender-100 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <Mail className="w-5 h-5 text-lavender-600" />
                    </div>
                    <div>
                      <p
                        className="text-sm text-lavender-500 mb-1"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        Email
                      </p>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="text-grape-700 hover:text-lavender-600 transition-colors font-medium"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        {siteConfig.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-lavender-100 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <MapPin className="w-5 h-5 text-lavender-600" />
                    </div>
                    <div>
                      <p
                        className="text-sm text-lavender-500 mb-1"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        Location
                      </p>
                      <p
                        className="text-grape-700 font-medium"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        {siteConfig.location}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quote */}
                <div className="mt-8 pt-8 border-t border-lavender-100">
                  <p
                    className="text-grape-600/80 text-sm italic leading-relaxed"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    "For with God nothing shall be impossible."
                  </p>
                  <p
                    className="text-lavender-500 text-xs mt-2"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  >
                    — Luke 1:37
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-br from-violet-300/30 to-transparent rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative bg-gradient-to-br from-lavender-500 to-violet-600 rounded-2xl p-6 shadow-xl">
                <p
                  className="text-white/90 font-medium mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}
                >
                  Connect with Hem of Grace Events
                </p>
                <div className="flex space-x-3">
                  <a
                    href={siteConfig.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on Instagram"
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href={siteConfig.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on TikTok"
                  >
                    <Music2 className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href={siteConfig.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on Facebook"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-lavender-200/20 to-transparent rounded-3xl blur-lg" />
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="relative bg-white rounded-2xl p-8 shadow-xl shadow-lavender-900/10 border border-lavender-100/50"
              >
                {/* Netlify hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don’t fill this out: <input name="bot-field" />
                  </label>
                </p>
                <input type="hidden" name="eventType" value={formData.eventType} />
                <input type="hidden" name="eventLocation" value="Florida" />

                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Name <span className="text-violet-400">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-lavender-400" />
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                        className="pl-12 h-12 border-lavender-200 focus:border-lavender-400 focus:ring-lavender-400/20 rounded-xl shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Email <span className="text-violet-400">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-lavender-400" />
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="pl-12 h-12 border-lavender-200 focus:border-lavender-400 focus:ring-lavender-400/20 rounded-xl shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Phone <span className="text-violet-400">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-lavender-400" />
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="(555) 123-4567"
                        className="pl-12 h-12 border-lavender-200 focus:border-lavender-400 focus:ring-lavender-400/20 rounded-xl shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Event Type */}
                  <div>
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Event Type <span className="text-violet-400">*</span>
                    </label>
                    <Select value={formData.eventType} onValueChange={handleSelectChange}>
                      <SelectTrigger className="h-12 border-lavender-200 focus:border-lavender-400 rounded-xl shadow-sm">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        {eventTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Event Date */}
                  <div className="sm:col-span-2">
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Event Date <span className="text-violet-400">*</span>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-lavender-400" />
                      <Input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        required
                        className="pl-12 h-12 border-lavender-200 focus:border-lavender-400 focus:ring-lavender-400/20 rounded-xl shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <label
                      className="block text-grape-700 text-sm font-medium mb-2"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      Message <span className="text-violet-400">*</span>
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-lavender-400" />
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us about your event vision, guest count, venue, and any special requests..."
                        rows={4}
                        className="pl-12 pt-4 border-lavender-200 focus:border-lavender-400 focus:ring-lavender-400/20 rounded-xl shadow-sm resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {submitError && (
                  <div
                    className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-xl text-violet-700 text-sm"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  >
                    {submitError}
                  </div>
                )}

                {/* Submit Button */}
                <div className="mt-8">
                  <div className="relative inline-block">
                    <div className="absolute -inset-1 bg-gradient-to-r from-lavender-400/60 via-violet-400/50 to-lavender-400/60 rounded-full blur-lg opacity-70" />
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="relative w-full sm:w-auto px-10 py-6 btn-shine bg-gradient-to-r from-lavender-500 to-violet-600 hover:from-lavender-600 hover:to-violet-700 text-white rounded-full font-medium text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem' }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          Request a Consultation
                          <Send className="ml-3 w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Inline success message */}
                {submitMessage && !showSuccess && (
                  <div className="mt-6 p-4 bg-lavender-50 border border-lavender-200 rounded-xl flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-lavender-600 flex-shrink-0 mt-0.5" />
                    <p
                      className="text-lavender-800 text-sm"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      {submitMessage}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md rounded-2xl border-lavender-200">
          <DialogHeader>
            <div className="mx-auto">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-radial from-lavender-200/50 to-transparent rounded-full blur-xl" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-lavender-400 to-violet-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              </div>
            </div>
            <DialogTitle
              className="text-center text-2xl text-grape-800 mt-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Thank You!
            </DialogTitle>
            <DialogDescription
              className="text-center text-base text-grape-600 leading-relaxed"
              style={{ fontFamily: "'Lato', sans-serif" }}
            >
              Your inquiry has been received. We'll be in touch soon to start planning your beautiful
              event.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-6">
            <Button
              onClick={() => setShowSuccess(false)}
              className="bg-gradient-to-r from-lavender-500 to-violet-600 hover:from-lavender-600 hover:to-violet-700 text-white rounded-full px-8 py-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Contact;

